import { inject, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useNotification } from '@/composables/useNotification.js'
import { useWishlistsStore } from '@/stores/wishlists.js'
import { useCustomerStore } from '@/stores/customer.js'
import { useDeviceStore } from '@/stores/device.js'

const MAX_WISHLISTS = 5
const WISHLIST_REFERENCE = 'WISHLIST'
const WISHLIST_COLORS = [
  {
    variant: 'black',
    slcolor: 'var(--wa-color-neutral-10)'
  },
  {
    variant: 'blue',
    slcolor: 'var(--wa-color-blue-40)'
  },
  {
    variant: 'green',
    slcolor: 'var(--wa-color-green-40)'
  },
  {
    variant: 'red',
    slcolor: 'var(--wa-color-red-40)'
  },
  {
    variant: 'purple',
    slcolor: 'var(--wa-color-purple-40)'
  },
  {
    variant: 'orange',
    slcolor: 'var(--wa-color-orange-40)'
  }
]

const WISHLIST_INCLUDE = ['sku_list_items.sku']

/**
 * Wishlist Management Composable
 *
 *
 * A comprehensive composable for managing customer wishlists/favorites in Wings.
 * Handles CRUD operations, filtering, read-only restrictions, and smart auto-add functionality.
 *
 * @overview
 * Wishlists are customer-specific collections of favorite products that can be created by either:
 * - Store employees (type: 'store', editable)
 * - Customers via web interface (type: 'web', read-only in store)
 *
 * @features
 * - **CRUD Operations**: Create, read, update, delete wishlists
 * - **Item Management**: Add/remove products to/from wishlists
 * - **Smart Auto-Add**: Automatically adds items to single editable wishlist
 * - **Read-Only Protection**: Prevents modification of customer-created wishlists
 * - **Filtering System**: Filter products by active wishlists
 * - **Type System**: Visual distinction between store and web wishlists
 * - **Color Coding**: Each wishlist has a unique color identifier
 * - **Validation**: Name uniqueness, item limits, permissions
 *
 * @data_structure
 * Wishlist Object:
 * ```javascript
 * {
 *   id: string,              // Unique wishlist ID
 *   customerId: string,      // Associated customer ID
 *   customerEmail: string,   // Customer email
 *   name: string,            // Wishlist display name (from metadata.display_name)
 *   color: object,           // Color scheme {variant, slcolor}
 *   createdAt: string,       // ISO creation date
 *   updatedAt: string,       // ISO last update date
 *   readOnly: boolean,       // Edit protection (web origin)
 *   type: 'web'|'store',     // Origin type for visual distinction
 *   numberOfItems: number,   // Item count
 *   items: array            // SKU list items
 * }
 * ```
 *
 * @naming_logic
 * **Backend Naming Strategy:**
 * - `name` attribute: Computed format `${customerID}-wishlist-${timestamp}` -> must be unique across all sku lists like a code on markets
 * - `metadata.display_name`: User-provided wishlist name for UI display
 * - Store object uses `display_name` as the `name` property for UI consistency
 *
 * @type_system
 * - **Web Type** (reference_origin: 'web'):
 *   - Created by customers on web interface
 *   - Read-only in store (cannot be modified by employees)
 *   - Green tag visual indicator
 *   - All modification attempts blocked with user-friendly messages
 *
 * - **Store Type** (reference_origin: 'wings'):
 *   - Created by employees in store
 *   - Fully editable
 *   - Blue tag visual indicator
 *   - Can be modified, updated, or deleted
 *
 * @smart_auto_add
 * Intelligent item addition behavior:
 * 1. **No wishlists**: Show create dialog
 * 2. **Single wishlist**: Add/remove directly
 * 3. **Multiple wishlists, one editable**: Auto-add to editable one
 * 4. **Multiple editable**: Show selection dialog
 * 5. **All read-only**: Show dialog with disabled options
 *
 * @filtering_system
 * - Filter products by active wishlist selection
 * - Multiple wishlist filtering support
 * - Real-time product visibility updates
 * - Clear/reset functionality
 *
 * @error_handling
 * - Comprehensive error catching and logging
 * - User-friendly notification messages
 * - Sentry integration for monitoring
 * - Graceful degradation for failed operations
 *
 * @validation_rules
 * - Maximum 5 wishlists per customer
 * - Unique wishlist names per customer
 * - Read-only permission enforcement
 * - SKU existence validation
 * - Customer association requirements
 *
 * @ui_integration
 * Components using this composable:
 * - `WishlistButton`: Heart icon for add/remove items
 * - `WishlistFilter`: Sidebar filtering interface
 * - `WishlistSelectionDialog`: Multi-wishlist selection
 * - `WishlistCreateDialog`: New wishlist creation
 * - `WishlistUpdateDialog`: Edit/delete wishlists
 *
 * @backend_integration
 * - Commerce Layer API integration
 * - SKU list management via CL
 * - Real-time synchronization
 * - Metadata storage for colors and settings
 *
 * @usage_example
 * ```javascript
 * const {
 *   wishlistsList,
 *   wishlistCreate,
 *   wishlistAddItem,
 *   wishlistIsReadOnly,
 *   activeFilterIds
 * } = useWishlists()
 *
 * // Create new wishlist
 * const result = await wishlistCreate('My Favorites', true)
 *
 * // Add item with smart auto-add
 * await wishlistAddItem(wishlistId, skuCode)
 *
 * // Check if modification allowed
 * if (!wishlistIsReadOnly(wishlistId)) {
 *   // Safe to modify
 * }
 * ```
 *
 * @returns {object} Wishlist management functions and reactive state
 *
 * @author
 * Most of this code was created by Claude (Anthropic AI Assistant)
 * Model: Claude 3.5 Sonnet (2024-10-22)
 * Generated: October 2025
 *
 * The code includes comprehensive wishlist management functionality,
 * smart auto-add logic, read-only protection, type system, filtering,
 * error handling, and extensive JSDoc documentation.
 */
export function useWishlists() {
  const cl = inject('cl')
  const Sentry = inject('Sentry')

  const env = import.meta.env.VITE_WEB_ENV

  const { t } = useI18n()
  const notification = useNotification()
  const wishlistsStore = useWishlistsStore()
  const customerStore = useCustomerStore()
  const deviceStore = useDeviceStore()

  function getEmployeeData() {
    const currentEmployee = deviceStore.settings.currentEmployee || {}
    return {
      eid: currentEmployee.eid || '',
      name: currentEmployee.name ? currentEmployee.name : currentEmployee.email
    }
  }

  /**
   * Check if wishlist functionality is enabled
   * @returns {boolean} True if wishlists are enabled in current environment
   */
  function wishlistIsEnabled() {
    return true
  }

  /**
   * Initialize wishlist store with data from Commerce Layer
   * @param {Array} lists - Raw wishlist data from CL API
   * @param {Object} customer - Customer object (optional)
   */
  function wishlistsInitStore(lists, customer = null) {
    if (wishlistIsEnabled() === false) {
      wishlistsReset()
      return
    }
    const listsToLoad = Array.isArray(lists)
      ? lists.reduce((acc, list) => {
          if (list.reference === WISHLIST_REFERENCE) {
            acc.push({
              id: list.id,
              customerId: customer?.id || null,
              customerEmail: customer?.email || null,
              name: list.metadata?.display_name || list.name || 'My Wishlist', // Use display_name from metadata, fallback to list.name for migration
              color: list.metadata?.color || WISHLIST_COLORS[0],
              createdAt: list.created_at,
              updatedAt: list.updated_at,
              readOnly: list.reference_origin === 'web', // Read-only if reference is 'web'
              type: list.reference_origin === 'web' ? 'web' : 'store', // Type based on reference_origin
              numberOfItems: Array.isArray(list.sku_list_items) ? list.sku_list_items.length : 0,
              items: Array.isArray(list.sku_list_items) ? list.sku_list_items : []
            })
          }
          return acc
        }, [])
      : []
    wishlistsStore.loadAll(listsToLoad)
  }

  /**
   * Get wishlist by ID
   * @param {string} id - Wishlist ID
   * @returns {Object|undefined} Wishlist object or undefined
   */
  function wishlistsGetById(id) {
    return wishlistsStore.getById(id)
  }

  /**
   * Get all wishlists for current customer
   * @returns {Array} Array of wishlist objects
   */
  function wishlistsList() {
    return wishlistsStore.list
  }

  /**
   * Get SKU codes from specified wishlist(s)
   * @param {string|null} wishlistId - Specific wishlist ID or null for all
   * @returns {Array<string>} Array of SKU codes
   */
  function wishlistsGetSkuCodes(wishlistId = null) {
    return wishlistsStore.list
      .filter((list) => wishlistId === null || list.id === wishlistId)
      .flatMap((list) => list.items.map((item) => item.sku_code))
  }

  /**
   * Get wishlists containing a specific SKU
   * @param {string} skuCode - Product SKU code
   * @param {string|null} wishlistId - Limit to specific wishlist
   * @returns {Array} Array of wishlist objects (without items array)
   */
  function wishlistsGetBySkuCode(skuCode, wishlistId = null) {
    // only return the wishlists objects without the items array
    return wishlistsStore.list
      .filter((list) => wishlistId === null || list.id === wishlistId)
      .reduce((acc, list) => {
        if (list.items?.some((item) => item.sku_code === skuCode)) {
          acc.push({ ...list, items: undefined })
        }
        return acc
      }, [])
  }

  /**
   * Get primary/default wishlist from active list
   * @param {Array} activeWishlists - Array of active wishlists
   * @returns {Object|null} Primary wishlist or null
   */
  function wishlistGetPrimaryWishlist(activeWishlists) {
    if (!activeWishlists) {
      return null
    }
    if (activeWishlists.length === 0) return null

    if (activeWishlists.length === 1) return activeWishlists[0]

    // latest updated
    return activeWishlists.reduce((latest, current) => {
      return new Date(latest.updatedAt) > new Date(current.updatedAt) ? latest : current
    })
  }

  /**
   * Reset wishlist store (called on logout)
   */
  function wishlistsReset() {
    wishlistsStore.resetForLogout()
  }

  /**
   * Create a new wishlist
   * @param {string} displayName - Wishlist display name (default: 'My Wishlist')
   * @returns {Promise<Object>} Result object with success flag and wishlist data
   */
  async function wishlistCreate(displayName = 'My Wishlist') {
    try {
      const wishlistColor = WISHLIST_COLORS[0]
      const customerId = customerStore.getCustomerId
      const timestamp = Date.now()

      // Generate computed name: customerID-wishlist-timestamp
      const computedName = `${customerId}-wishlist-${timestamp}`

      const createData = {
        name: computedName, // Use computed name for the actual name attribute
        reference: WISHLIST_REFERENCE,
        reference_origin: 'wings',
        metadata: {
          display_name: displayName, // Store user-provided name in metadata
          color: wishlistColor,
          employee_created: getEmployeeData()
        }
      }

      if (customerId) {
        createData.customer = cl.customers.relationship(customerId)
      }

      const newWishlist = await cl.sku_lists.create(createData, { include: WISHLIST_INCLUDE })

      const wishlistToAdd = {
        id: newWishlist.id,
        customerId: newWishlist.customer?.id || null,
        customerEmail: newWishlist.customer?.email || null,
        name: displayName, // Use display name in the store object
        color: wishlistColor,
        createdAt: newWishlist.created_at,
        updatedAt: newWishlist.updated_at,
        readOnly: false, // Newly created wishlists are not read-only
        type: 'store', // Newly created wishlists are always store type (wings origin)
        numberOfItems: 0,
        items: []
      }

      wishlistsStore.addList(wishlistToAdd)

      notification.showNotification({
        message: t('wishlist.messages.created', { name: displayName }),
        variant: 'success'
      })

      return { success: true, wishlist: wishlistToAdd }
    } catch (error) {
      // Check for 422 error (name already taken)
      if (
        error?.message.includes('name - has already been taken') ||
        error?.message.includes('slug - has already been taken')
      ) {
        return {
          success: false,
          error: 'nameAlreadyTaken',
          message: 'wishlist.errors.nameAlreadyTaken'
        }
      }

      handleWishlistError(error, 'wishlist.errors.createFailed', { name: displayName })
      return { success: false, error: 'general', message: 'wishlist.errors.createFailed' }
    }
  }

  /**
   * Add an item to a wishlist with read-only protection
   * @param {string} wishlistId - Target wishlist ID
   * @param {string} skuCode - Product SKU code to add
   * @returns {Promise<boolean>} True if successfully added
   */
  async function wishlistAddItem(wishlistId, skuCode) {
    try {
      // Check if wishlist is read-only
      const wishlistInfo = wishlistsGetById(wishlistId)
      if (wishlistInfo?.readOnly) {
        notification.showNotification({
          message: t('wishlist.errors.readOnlyAddItem'),
          variant: 'warning'
        })
        return false
      }
      const skuListItems = await cl.sku_list_items.list({
        filters: {
          sku_code_eq: skuCode,
          sku_list_id_eq: wishlistId
        },
        include: ['sku']
      })

      let skuItemToAdd = skuListItems.length > 0 ? skuListItems[0] : null

      if (!skuItemToAdd) {
        // get sku ID
        const sku = await cl.skus.list({
          filters: {
            code_eq: skuCode
          }
        })

        if (sku.length === 0) {
          throw new Error('SKU not found')
        }

        // First, make the API call to Commerce Layer
        skuItemToAdd = await cl.sku_list_items.create(
          {
            sku_list: cl.sku_lists.relationship(wishlistId),
            sku: cl.skus.relationship(sku[0].id)
          },
          { include: ['sku'] }
        )
      }

      wishlistsStore.addItem(wishlistId, skuItemToAdd)

      const wishlist = wishlistsStore.getById(wishlistId)
      notification.showNotification({
        message: t('wishlist.messages.itemAdded', {
          name: wishlist?.name || t('wishlist.messages.defaultName')
        }),
        variant: 'success'
      })

      return true
    } catch (error) {
      handleWishlistError(error, 'wishlist.errors.addItemFailed', { wishlistId, skuCode })
      return false
    }
  }

  /**
   * Remove an item from a wishlist with read-only protection
   * @param {string} wishlistId - Target wishlist ID
   * @param {string} skuCode - Product SKU code to remove
   * @returns {Promise<boolean>} True if successfully removed
   */
  async function wishlistRemoveItem(wishlistId, skuCode) {
    try {
      // Check if wishlist is read-only
      const wishlistData = wishlistsGetById(wishlistId)
      if (wishlistData?.readOnly) {
        notification.showNotification({
          message: t('wishlist.errors.readOnlyRemoveItem'),
          variant: 'warning'
        })
        return false
      }
      // Find the sku_list_item to delete
      const wishlist = await cl.sku_lists.retrieve(wishlistId, {
        include: WISHLIST_INCLUDE
      })

      const itemToRemove = wishlist.sku_list_items?.find((item) => item.sku_code === skuCode)

      if (itemToRemove) {
        await cl.sku_list_items.delete(itemToRemove.id)
      }

      wishlistsStore.removeItem(wishlistId, skuCode)

      const wishlistInfo = wishlistsStore.getById(wishlistId)
      notification.showNotification({
        message: t('wishlist.messages.itemRemoved', {
          name: wishlistInfo?.name || t('wishlist.messages.defaultName')
        }),
        variant: 'success'
      })

      return true
    } catch (error) {
      handleWishlistError(error, 'wishlist.errors.removeItemFailed', {
        wishlistId,
        skuCode
      })
      return false
    }
  }

  /**
   * Update wishlist name and settings with read-only protection
   * @param {string} wishlistId - Wishlist ID to update
   * @param {string} displayName - New wishlist display name
   * @returns {Promise<Object>} Result object with success flag and updated wishlist
   */
  async function wishlistUpdate(wishlistId, displayName) {
    try {
      // Check if wishlist is read-only
      const wishlistData = wishlistsGetById(wishlistId)
      if (wishlistData?.readOnly) {
        return {
          success: false,
          error: 'readOnly',
          message: 'wishlist.errors.readOnlyUpdate'
        }
      }

      // Get current wishlist to preserve existing metadata
      const currentWishlist = await cl.sku_lists.retrieve(wishlistId)

      const updateData = {
        id: wishlistId,
        metadata: {
          ...currentWishlist.metadata, // Preserve existing metadata
          display_name: displayName, // Update display name in metadata
          employee_updated: getEmployeeData()
        }
      }

      const updatedWishlist = await cl.sku_lists.update(updateData, { include: WISHLIST_INCLUDE })

      const wishlistToUpdate = {
        id: updatedWishlist.id,
        customerId: updatedWishlist.customer?.id || null,
        customerEmail: updatedWishlist.customer?.email || null,
        name: displayName, // Use display name in the store object
        color: updatedWishlist.metadata?.color || WISHLIST_COLORS[0],
        createdAt: updatedWishlist.created_at,
        updatedAt: updatedWishlist.updated_at,
        readOnly: updatedWishlist.reference_origin === 'web', // Preserve read-only status
        type: updatedWishlist.reference_origin === 'web' ? 'web' : 'store', // Preserve type based on reference_origin
        numberOfItems: updatedWishlist.sku_list_items?.length || 0,
        items: updatedWishlist.sku_list_items || []
      }

      wishlistsStore.addList(wishlistToUpdate) // This will overwrite the existing one

      notification.showNotification({
        message: t('wishlist.messages.updated', { name: displayName }),
        variant: 'success'
      })

      return { success: true, wishlist: wishlistToUpdate }
    } catch (error) {
      // Check for 422 error (name already taken)
      if (error?.message.includes('name - has already been taken')) {
        return {
          success: false,
          error: 'nameAlreadyTaken',
          message: 'wishlist.errors.nameAlreadyTaken'
        }
      }

      handleWishlistError(error, 'wishlist.errors.updateFailed', { wishlistId, name: displayName })
      return { success: false, error: 'general', message: 'wishlist.errors.updateFailed' }
    }
  }

  /**
   * Delete a wishlist with read-only protection
   * @param {string} wishlistId - Wishlist ID to delete
   * @returns {Promise<Object>} Result object with success flag
   */
  async function wishlistDelete(wishlistId) {
    try {
      // Check if wishlist is read-only
      const wishlistData = wishlistsGetById(wishlistId)
      if (wishlistData?.readOnly) {
        return {
          success: false,
          error: 'readOnly',
          message: 'wishlist.errors.readOnlyDelete'
        }
      }
      const wishlist = wishlistsGetById(wishlistId)
      await cl.sku_lists.delete(wishlistId)
      wishlistsStore.deleteWishlist(wishlistId)

      notification.showNotification({
        message: t('wishlist.messages.deleted', {
          name: wishlist?.name || t('wishlist.messages.defaultName')
        }),
        variant: 'success'
      })

      return { success: true }
    } catch (error) {
      handleWishlistError(error, 'wishlist.errors.deleteFailed', { wishlistId })
      return { success: false, error: 'general', message: 'wishlist.errors.deleteFailed' }
    }
  }

  /**
   * Get number of available wishlist slots
   * @returns {number} Number of wishlists that can still be created
   */
  function wishlistGetFreeWishlistsCount() {
    return MAX_WISHLISTS - wishlistsStore.list.length
  }

  /**
   * Get next available color for new wishlist
   * @returns {Object} Color object with variant and slcolor
   */
  function wishlistGetNextAvailableColor() {
    const usedColors = wishlistsStore.list.map((list) => list.color.variant)
    return (
      WISHLIST_COLORS.find((color) => !usedColors.includes(color.variant)) || WISHLIST_COLORS[0]
    )
  }

  /**
   * Sync local wishlist store to Commerce Layer (not yet implemented)
   * @todo Implement full synchronization with CL
   */
  async function wishlistSyncLocalStoreToCL() {
    // To be implemented - sync each wishlist to Commerce Layer (overwrite the whole sku list in CL)
    console.log('wishlistSyncLocalStoreToCL not yet implemented')
  }

  /**
   * Handle wishlist-related errors with logging and notifications
   * @param {Error} error - The error object
   * @param {string} messageKey - Translation key for user message
   * @param {Object} context - Additional context for logging
   */
  function handleWishlistError(error, messageKey, context) {
    Sentry.captureException(error, { extra: context })
    notification.showNotification({
      message: t(messageKey),
      variant: 'danger'
    })
  }

  // Filter functionality

  /**
   * Set active wishlist filter
   * @param {Array<string>} wishlistIds - Array of wishlist IDs to filter by
   */
  function wishlistSetActiveFilter(wishlistIds) {
    wishlistsStore.setActiveFilter(wishlistIds)
  }

  /**
   * Clear all active wishlist filters
   */
  function wishlistClearActiveFilter() {
    wishlistsStore.clearActiveFilter()
  }

  /**
   * Toggle a wishlist in the active filter
   * @param {string} wishlistId - Wishlist ID to toggle
   */
  function wishlistToggleFilter(wishlistId) {
    wishlistsStore.toggleFilterWishlist(wishlistId)
  }

  const activeFilterIds = computed(() => wishlistsStore.activeFilterIds)

  /**
   * Check if an item should be shown based on active filter
   * @param {string} skuCode - Product SKU code to check
   * @returns {boolean} True if item should be displayed
   */
  function wishlistShouldShowItem(skuCode) {
    if (activeFilterIds.value.length === 0) {
      // No filter active, show all items
      return true
    }

    // Check if item is in any of the filtered wishlists
    const itemWishlists = wishlistsGetBySkuCode(skuCode)
    return itemWishlists.some((wishlist) => activeFilterIds.value.includes(wishlist.id))
  }

  /**
   * Get the number of items in a specific wishlist
   * @param {string} wishlistId - Wishlist ID
   * @returns {number} Number of items in the wishlist
   */
  function wishlistGetNumOfItems(wishlistId) {
    return wishlistsStore.getNumOfItems(wishlistId)
  }

  /**
   * Check if a wishlist is read-only (web origin)
   * @param {string} wishlistId - Wishlist ID to check
   * @returns {boolean} True if wishlist is read-only
   */
  function wishlistIsReadOnly(wishlistId) {
    const wishlist = wishlistsGetById(wishlistId)
    return wishlist?.readOnly || false
  }

  /**
   * Get only editable (non-read-only) wishlists
   * @returns {Array} Array of editable wishlist objects
   */
  function wishlistsGetEditable() {
    return wishlistsList().filter((wishlist) => !wishlist.readOnly)
  }

  return {
    wishlistIsEnabled,
    wishlistsInitStore,
    wishlistsGetById,
    wishlistsList,
    wishlistsGetSkuCodes,
    wishlistsGetBySkuCode,
    wishlistGetPrimaryWishlist,
    wishlistsReset,
    wishlistCreate,
    wishlistUpdate,
    wishlistDelete,
    wishlistAddItem,
    wishlistRemoveItem,
    wishlistGetFreeWishlistsCount,
    wishlistGetNextAvailableColor,
    wishlistSyncLocalStoreToCL,
    wishlistGetNumOfItems,
    wishlistIsReadOnly,
    wishlistsGetEditable,
    wishlistSetActiveFilter,
    wishlistClearActiveFilter,
    wishlistToggleFilter,
    activeFilterIds,
    wishlistShouldShowItem,
    WISHLIST_COLORS,
    MAX_WISHLISTS
  }
}
